import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, MapPin, Users, Building2, Edit, UserCog, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface Site {
  id: string;
  location_name: string;
  site_code: string | null;
  description: string | null;
  address: string | null;
  subscription_tier: string;
  subscription_status: string;
  max_workers: number;
  is_active: boolean;
  created_at: string;
  worker_count?: number;
  ai_conversations_limit: number;
  ai_conversations_used: number;
}

interface SiteAdmin {
  id: string;
  user_id: string;
  display_name: string;
  full_name: string | null;
  email?: string;
}

const AdminSites = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAssignAdminDialogOpen, setIsAssignAdminDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [selectedSiteForAdmin, setSelectedSiteForAdmin] = useState<Site | null>(null);
  const [siteAdmins, setSiteAdmins] = useState<SiteAdmin[]>([]);
  const [availableUsers, setAvailableUsers] = useState<SiteAdmin[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [newSite, setNewSite] = useState({
    location_name: '',
    site_code: '',
    description: '',
    address: '',
    max_workers: 50,
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    setIsLoading(true);
    try {
      const { data: sitesData, error } = await supabase
        .from('company_locations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const sitesWithCounts = await Promise.all(
        (sitesData || []).map(async (site) => {
          const { count } = await supabase
            .from('worker_profiles')
            .select('*', { count: 'exact', head: true })
            .eq('site_id', site.id);
          
          return { ...site, worker_count: count || 0 };
        })
      );

      setSites(sitesWithCounts);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSite = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Get the current user's company_id
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile, error: profileError } = await supabase
        .from('worker_profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profile?.company_id) {
        throw new Error('Could not determine company');
      }

      const { error } = await supabase
        .from('company_locations')
        .insert({
          company_id: profile.company_id,
          location_name: newSite.location_name,
          site_code: newSite.site_code || null,
          description: newSite.description || null,
          address: newSite.address || null,
          max_workers: newSite.max_workers,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Site added successfully",
      });

      setIsDialogOpen(false);
      setNewSite({
        location_name: '',
        site_code: '',
        description: '',
        address: '',
        max_workers: 50,
      });
      fetchSites();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSite) return;
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('company_locations')
        .update({
          location_name: editingSite.location_name,
          site_code: editingSite.site_code,
          description: editingSite.description,
          address: editingSite.address,
          max_workers: editingSite.max_workers,
          is_active: editingSite.is_active,
        })
        .eq('id', editingSite.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Site updated successfully",
      });

      setIsEditDialogOpen(false);
      setEditingSite(null);
      fetchSites();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openEditDialog = (site: Site) => {
    setEditingSite({ ...site });
    setIsEditDialogOpen(true);
  };

  const openAssignAdminDialog = async (site: Site) => {
    setSelectedSiteForAdmin(site);
    setIsAssignAdminDialogOpen(true);
    await fetchSiteAdmins(site.id);
    await fetchAvailableUsers(site.id);
  };

  const fetchSiteAdmins = async (siteId: string) => {
    try {
      const { data: assignments, error } = await supabase
        .from('site_admin_assignments')
        .select('id, user_id')
        .eq('site_id', siteId);

      if (error) throw error;

      if (assignments && assignments.length > 0) {
        const userIds = assignments.map(a => a.user_id);
        const { data: profiles } = await supabase
          .from('worker_profiles')
          .select('id, user_id, display_name, full_name')
          .in('user_id', userIds);

        const admins: SiteAdmin[] = (assignments || []).map(a => {
          const profile = profiles?.find(p => p.user_id === a.user_id);
          return {
            id: a.id,
            user_id: a.user_id,
            display_name: profile?.display_name || 'Unknown',
            full_name: profile?.full_name || null,
          };
        });

        setSiteAdmins(admins);
      } else {
        setSiteAdmins([]);
      }
    } catch (error) {
      console.error('Error fetching site admins:', error);
      setSiteAdmins([]);
    }
  };

  const fetchAvailableUsers = async (siteId: string) => {
    try {
      // Get existing site admin user IDs for this site
      const { data: existingAssignments } = await supabase
        .from('site_admin_assignments')
        .select('user_id')
        .eq('site_id', siteId);

      const existingUserIds = (existingAssignments || []).map(a => a.user_id);

      // Get all workers who could be site admins
      const { data: profiles, error } = await supabase
        .from('worker_profiles')
        .select('id, user_id, display_name, full_name')
        .order('display_name', { ascending: true });

      if (error) throw error;

      // Filter out users who are already site admins for this site
      const available = (profiles || [])
        .filter(p => p.user_id && !existingUserIds.includes(p.user_id))
        .map(p => ({
          id: p.id,
          user_id: p.user_id,
          display_name: p.display_name,
          full_name: p.full_name,
        }));

      setAvailableUsers(available);
    } catch (error) {
      console.error('Error fetching available users:', error);
      setAvailableUsers([]);
    }
  };

  const handleAssignSiteAdmin = async () => {
    if (!selectedSiteForAdmin || !selectedUserId) return;
    setIsLoading(true);

    try {
      // First, ensure user has site_admin role
      const { data: existingRole } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', selectedUserId)
        .eq('role', 'site_admin')
        .maybeSingle();

      if (!existingRole) {
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({ user_id: selectedUserId, role: 'site_admin' });

        if (roleError) throw roleError;
      }

      // Create site admin assignment
      const { error: assignError } = await supabase
        .from('site_admin_assignments')
        .insert({
          user_id: selectedUserId,
          site_id: selectedSiteForAdmin.id,
        });

      if (assignError) throw assignError;

      toast({
        title: "Success",
        description: "Site admin assigned successfully",
      });

      setSelectedUserId('');
      await fetchSiteAdmins(selectedSiteForAdmin.id);
      await fetchAvailableUsers(selectedSiteForAdmin.id);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveSiteAdmin = async (assignmentId: string) => {
    if (!selectedSiteForAdmin) return;
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('site_admin_assignments')
        .delete()
        .eq('id', assignmentId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Site admin removed successfully",
      });

      await fetchSiteAdmins(selectedSiteForAdmin.id);
      await fetchAvailableUsers(selectedSiteForAdmin.id);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'professional':
        return <Badge className="bg-primary text-primary-foreground">Professional</Badge>;
      case 'enterprise':
        return <Badge className="bg-success text-success-foreground">Enterprise</Badge>;
      default:
        return <Badge variant="secondary">Starter</Badge>;
    }
  };

  const getStatusBadge = (status: string, isActive: boolean) => {
    if (!isActive) return <Badge variant="destructive">Inactive</Badge>;
    switch (status) {
      case 'active':
        return <Badge className="bg-success text-success-foreground">Active</Badge>;
      case 'trial':
        return <Badge className="bg-warning text-warning-foreground">Trial</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const totalWorkers = sites.reduce((sum, site) => sum + (site.worker_count || 0), 0);
  const totalCapacity = sites.reduce((sum, site) => sum + site.max_workers, 0);
  const activeSites = sites.filter(s => s.is_active).length;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Sites</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">{sites.length}</span>
              <span className="text-sm text-muted-foreground">({activeSites} active)</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Workers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">{totalWorkers}</span>
              <span className="text-sm text-muted-foreground">/ {totalCapacity} capacity</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Capacity Used</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">{totalCapacity > 0 ? Math.round((totalWorkers / totalCapacity) * 100) : 0}%</span>
              </div>
              <Progress value={totalCapacity > 0 ? (totalWorkers / totalCapacity) * 100 : 0} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sites Table */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Company Sites</h3>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Site
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Site</DialogTitle>
              <DialogDescription>Create a new work site for your company</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddSite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="location_name">Site Name *</Label>
                <Input
                  id="location_name"
                  value={newSite.location_name}
                  onChange={(e) => setNewSite({ ...newSite, location_name: e.target.value })}
                  placeholder="e.g., Jakarta Main Plant"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="site_code">Site Code</Label>
                <Input
                  id="site_code"
                  value={newSite.site_code}
                  onChange={(e) => setNewSite({ ...newSite, site_code: e.target.value })}
                  placeholder="e.g., JKT-001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={newSite.address}
                  onChange={(e) => setNewSite({ ...newSite, address: e.target.value })}
                  placeholder="Full address"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newSite.description}
                  onChange={(e) => setNewSite({ ...newSite, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_workers">Max Workers</Label>
                <Input
                  id="max_workers"
                  type="number"
                  min="1"
                  value={newSite.max_workers}
                  onChange={(e) => setNewSite({ ...newSite, max_workers: parseInt(e.target.value) || 50 })}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Creating..." : "Create Site"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Site Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Workers</TableHead>
              <TableHead>AI Usage</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : sites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  No sites configured. Add your first site to get started.
                </TableCell>
              </TableRow>
            ) : (
              sites.map((site) => (
                <TableRow key={site.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{site.location_name}</p>
                      {site.address && <p className="text-xs text-muted-foreground">{site.address}</p>}
                    </div>
                  </TableCell>
                  <TableCell>{site.site_code || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{site.worker_count || 0} / {site.max_workers}</span>
                      <Progress 
                        value={((site.worker_count || 0) / site.max_workers) * 100} 
                        className="w-16 h-1.5" 
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{site.ai_conversations_used || 0} / {site.ai_conversations_limit}</span>
                      <Progress 
                        value={((site.ai_conversations_used || 0) / site.ai_conversations_limit) * 100} 
                        className="w-16 h-1.5" 
                      />
                    </div>
                  </TableCell>
                  <TableCell>{getTierBadge(site.subscription_tier)}</TableCell>
                  <TableCell>{getStatusBadge(site.subscription_status, site.is_active)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(site)}>
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openAssignAdminDialog(site)}>
                        <UserCog className="h-4 w-4 mr-1" />
                        Admins
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Site Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Site</DialogTitle>
            <DialogDescription>Update site information</DialogDescription>
          </DialogHeader>
          {editingSite && (
            <form onSubmit={handleEditSite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-location_name">Site Name *</Label>
                <Input
                  id="edit-location_name"
                  value={editingSite.location_name}
                  onChange={(e) => setEditingSite({ ...editingSite, location_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-site_code">Site Code</Label>
                <Input
                  id="edit-site_code"
                  value={editingSite.site_code || ''}
                  onChange={(e) => setEditingSite({ ...editingSite, site_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-address">Address</Label>
                <Textarea
                  id="edit-address"
                  value={editingSite.address || ''}
                  onChange={(e) => setEditingSite({ ...editingSite, address: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editingSite.description || ''}
                  onChange={(e) => setEditingSite({ ...editingSite, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-max_workers">Max Workers</Label>
                <Input
                  id="edit-max_workers"
                  type="number"
                  min="1"
                  value={editingSite.max_workers}
                  onChange={(e) => setEditingSite({ ...editingSite, max_workers: parseInt(e.target.value) || 50 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-is_active">Status</Label>
                <Select 
                  value={editingSite.is_active ? 'active' : 'inactive'} 
                  onValueChange={(value) => setEditingSite({ ...editingSite, is_active: value === 'active' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Updating..." : "Update Site"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Site Admin Dialog */}
      <Dialog open={isAssignAdminDialogOpen} onOpenChange={setIsAssignAdminDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Site Admins</DialogTitle>
            <DialogDescription>
              {selectedSiteForAdmin?.location_name} - Assign users as site administrators
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Site Admins */}
            <div>
              <Label className="text-sm font-medium">Current Site Admins</Label>
              {siteAdmins.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-2">No site admins assigned</p>
              ) : (
                <div className="space-y-2 mt-2">
                  {siteAdmins.map((admin) => (
                    <div key={admin.id} className="flex items-center justify-between p-2 bg-secondary rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{admin.display_name}</p>
                        {admin.full_name && (
                          <p className="text-xs text-muted-foreground">{admin.full_name}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSiteAdmin(admin.id)}
                        disabled={isLoading}
                      >
                        <X className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Site Admin */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Add Site Admin</Label>
              <div className="flex gap-2 mt-2">
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers.map((user) => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.display_name} {user.full_name ? `(${user.full_name})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={handleAssignSiteAdmin} 
                  disabled={!selectedUserId || isLoading}
                >
                  Assign
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSites;
